// A copy of one of Sayer's forecasts becomes the user's own the first time they do something only
// an owner can: Update now, or a change to its schedule or depth. There is no "Make it my own"
// step; the action converts the copy, then runs as it
// would on any forecast of theirs (commands/sayerForecasts.ts adopts it).
//
// A converted copy starts with no automatic updates, at the copy's depth: Update now runs once
// and leaves it so; a schedule change sets the schedule the user picked.
import { useCallback, useEffect, useRef } from "react";
import { isSayerManaged, observerOrigin } from "../lib/featured/observerCopy.js";
import { tagOwner } from "../lib/forecast/engines.js";
import { updateNowTier } from "../lib/forecast/headerActions.js";
import { pickedDepthOf } from "../lib/forecast/updateDepthPicks.js";
import { makeDefaultDeps } from "../commands/deps.js";
import { adoptSayerForecast } from "../commands/sayerForecasts.js";

/** Update now, in the forecast header: its intents' origin and surface. */
const UPDATE_NOW_AT = { origin: "timeline.reforecast", surface: "forecast-header" };
/** A schedule or depth change in the forecast's update settings (the control opens its own intent). */
const SCHEDULE_AT = { surface: "forecast-settings" };

/**
 * @param {object} deps
 * @param {object|null} deps.active the active run
 * @param {(opts?: {engine?: string, origin?: string, surface?: string}) => void} deps.reforecast
 *   useReforecast's
 * @param {(id: string, cadIdx: number, engine?: string, opts?: object) => void} deps.applySchedule
 *   useTrackingPrefs's
 * @param {(id: string) => object|undefined} deps.getForecast a forecast by client id
 * @returns {{updateNow: (run: object|null, tier?: "quick"|"deep") => void,
 *   applySchedule: (id: string, cadIdx: number, engine?: string, opts?: object) => void}}
 */
export function useSayerCopyOwnership({ active, reforecast, applySchedule, getForecast }) {
  const inFlight = useRef(new Map());
  // An Update now waiting for its conversion to land: {runId, engine}.
  const pendingUpdate = useRef(null);

  /** Make `f` the user's own; resolves true once it is (or already was). One request per copy. */
  const convert = useCallback((f, via, at) => {
    if (!isSayerManaged(f)) return Promise.resolve(true);
    const id = f.id ?? f.runId;
    if (inFlight.current.has(id)) return inFlight.current.get(id);
    const request = adoptSayerForecast(makeDefaultDeps(), { runId: id, via, ...at }).finally(() =>
      inFlight.current.delete(id),
    );
    inFlight.current.set(id, request);
    return request;
  }, []);

  // `tier` is a depth picked from Update now's menu, or else the one recorded for the forecast
  // (lib/forecast/updateDepthPicks.js, so the latest-forecast card's Update now honours a pick
  // made in the header); without one the update keeps the depth of the forecast's latest version.
  const updateNow = useCallback(
    (run, tier) => {
      const picked = tier || (run ? pickedDepthOf(run.id ?? run.runId) : undefined);
      if (!isSayerManaged(run)) {
        // A latest version on a forecaster with no depth leaves the choice to reforecast, which
        // runs the composer's selection.
        const depth = picked || updateNowTier(run);
        const engine = depth ? tagOwner(depth) : null;
        reforecast(engine ? { engine, ...UPDATE_NOW_AT } : UPDATE_NOW_AT);
        return;
      }
      // Unless a depth was picked, the first update stays on the forecaster Sayer used. Recorded
      // before the request, since the conversion's own store update is the render that runs it.
      const engine = (picked && tagOwner(picked)) || observerOrigin(run)?.lane || undefined;
      const runId = run.id ?? run.runId;
      pendingUpdate.current = { runId, engine };
      convert(run, "update_now", UPDATE_NOW_AT).then((ok) => {
        if (!ok && pendingUpdate.current?.runId === runId) pendingUpdate.current = null;
      });
    },
    [convert, reforecast],
  );

  // The update runs once the converted run is the one on screen, so the command reads the
  // user's own forecast rather than the copy it was a render ago.
  useEffect(() => {
    const p = pendingUpdate.current;
    if (!p || active?.id !== p.runId || isSayerManaged(active)) return;
    pendingUpdate.current = null;
    reforecast({ engine: p.engine, ...UPDATE_NOW_AT });
  }, [active, reforecast]);

  const applyScheduleOwned = useCallback(
    (id, cadIdx, engine, opts) => {
      const f = getForecast(id);
      if (!isSayerManaged(f)) {
        applySchedule(id, cadIdx, engine, opts);
        return;
      }
      // Kept at the copy's depth unless the user picked one.
      const tier = observerOrigin(f)?.tier;
      convert(f, "schedule", SCHEDULE_AT).then((ok) => {
        if (ok) applySchedule(id, cadIdx, engine || tier || undefined, opts);
        else opts?.intent?.end?.("error");
      });
    },
    [applySchedule, convert, getForecast],
  );

  return { updateNow, applySchedule: applyScheduleOwned };
}
